import { useState, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { ClbButton } from '@celebration/react/ClbButton/ClbButton';
import IOfferCardConfirmButtonProps from './IOfferCardConfirmButtonProps';
import { useOffersRound } from '../../../../../app/contexts/offers-round/offers-round.context';
import i18n from '../../../../../lib/i18n';
import ConfirmationModal from '../../../../../common/components/confirmation-modal';
import ConfirmationTermConstants from '../../../confirmation-term/constants';
import './offer-card-confirm-button.scss';

function OfferCardConfirmButton({
    offer,
    isConfirmationEnabled = true,
}: Readonly<IOfferCardConfirmButtonProps>): JSX.Element {
    const { confirmOffer } = useOffersRound();

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
        useState(false);
    const [isProcessingConfirmation, setIsProcessingConfirmation] =
        useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    const isConfirmationAvailable = useMemo(() => {
        if (!offer.confirmationAvailableAt) {
            return true;
        }
        const availableAt = new Date(offer.confirmationAvailableAt);
        return currentTime >= availableAt;
    }, [offer.confirmationAvailableAt, currentTime]);

    const confirmationAvailableAtFormatted = useMemo(() => {
        if (!offer.confirmationAvailableAt) {
            return null;
        }
        const availableAt = new Date(offer.confirmationAvailableAt);
        return availableAt.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    }, [offer.confirmationAvailableAt]);

    useEffect(() => {
        if (!offer.confirmationAvailableAt || isConfirmationAvailable) {
            return undefined;
        }

        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, [offer.confirmationAvailableAt, isConfirmationAvailable]);

    const handleOfferConfirmation = () => {
        setIsProcessingConfirmation(true);

        confirmOffer(offer.loadOfferId).then(() => {
            setIsProcessingConfirmation(false);
            setIsConfirmationModalOpen(false);
        });
    };

    const isButtonDisabled =
        isProcessingConfirmation || !isConfirmationAvailable;

    return (
        <>
            <div className="offer-confirm-button-container">
                <ClbButton
                    size="sm"
                    styleType="primary"
                    icon="check"
                    label={i18n.t('SmartCargas.Rounds.Confirm')}
                    data-testid="offer-footer-confirm-button"
                    onClick={() => setIsConfirmationModalOpen(true)}
                    loading={isProcessingConfirmation}
                    disabled={isButtonDisabled || !isConfirmationEnabled}
                    style={{
                        visibility:
                            offer.confirmed ||
                            offer.confirmedBy ||
                            !isConfirmationEnabled
                                ? 'hidden'
                                : 'visible',
                    }}
                />
                {!isConfirmationAvailable &&
                    confirmationAvailableAtFormatted && (
                        <span
                            className="confirmation-available-at-text"
                            data-testid="confirmation-available-at"
                        >
                            {i18n.t(
                                'SmartCargas.Rounds.ConfirmationAvailableAt',
                                {
                                    time: confirmationAvailableAtFormatted,
                                },
                            )}
                        </span>
                    )}
            </div>
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                title={i18n.t('ConfirmationModalTitle')}
                description={
                    <Trans
                        i18nKey="ConfirmationModalDescription"
                        values={{
                            loadId: offer.load.loadId,
                            vehicleDescription: offer.load.ratesByVehicle.find(
                                rate => rate.isAccepted,
                            )!.vehicle.description,
                        }}
                        components={{ bold: <strong /> }}
                    />
                }
                actionLink={
                    <a
                        href={`/${ConfirmationTermConstants.default}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="confirmation-modal-contract-link"
                    >
                        {i18n.t('ConfirmationModalContractLink')}
                    </a>
                }
                onConfirm={handleOfferConfirmation}
                onCancel={() => setIsConfirmationModalOpen(false)}
                isLoading={isProcessingConfirmation}
            />
        </>
    );
}

export default OfferCardConfirmButton;
